import type { AccountSraDto } from '../../types/api';

/**
 * Master-account rules for Account > Manage Team.
 *
 * - The master SRA is always a Dashboard (AccessType 11); only when the account has no Dashboard
 *   can it be a Pro Dashboard (AccessType 10). No other AccessType may ever be the master.
 * - Pro subscriptions start with a single Pro Dashboard that is the master; upgrading to Groups
 *   changes that same SRA to a Dashboard (11) and it stays the master (the flag is on the SRA).
 * - A master is only ever auto-assigned when NO eligible SRA is flagged as master.
 * - "Reassign Master Account" may only target Dashboard (11) SRAs.
 */

export const MASTER_ACCOUNT_SETTING = 'masteraccount';

export const DASHBOARD_ACCESS_TYPE = '11';
export const PRO_DASHBOARD_ACCESS_TYPE = '10';

const normalizeName = (value: string): string => value.trim().toLowerCase();

const isTruthy = (value: string | null | undefined): boolean => {
  const normalized = (value || '').trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes'
    || normalized === 'enabled' || normalized === 'active';
};

export const isFlaggedMaster = (row: AccountSraDto): boolean => {
  const entry = (row.ConfigInfo ?? []).find((setting) => normalizeName(setting.Name || '') === MASTER_ACCOUNT_SETTING);
  return isTruthy(entry?.Value);
};

const accessTypeOf = (row: AccountSraDto): string => (row.AccessType || '').trim();

/** Deleted accesses (Status <= -2) are never master candidates. */
const isDeleted = (row: AccountSraDto): boolean => {
  const status = Number.parseInt((row.Status || '').trim(), 10);
  return Number.isFinite(status) && status <= -2;
};

/** May this SRA hold the master designation at all? (Dashboard or Pro Dashboard only.) */
export const isMasterEligible = (row: AccountSraDto): boolean => {
  const type = accessTypeOf(row);
  return (type === DASHBOARD_ACCESS_TYPE || type === PRO_DASHBOARD_ACCESS_TYPE) && !isDeleted(row);
};

/** May "Reassign Master Account" target this SRA? (Dashboard only, and it must exist server-side.) */
export const isMasterReassignTarget = (row: AccountSraDto): boolean =>
  accessTypeOf(row) === DASHBOARD_ACCESS_TYPE && !isDeleted(row) && !!(row.SraId || '').trim();

const byPreference = (a: AccountSraDto, b: AccountSraDto): number => {
  // Dashboard (11) before Pro Dashboard (10).
  const rank = (row: AccountSraDto) => (accessTypeOf(row) === DASHBOARD_ACCESS_TYPE ? 0 : 1);
  return rank(a) - rank(b);
};

export interface MasterResolution {
  /** The row that is (or should become) the master, or null when no eligible SRA exists. */
  master: AccountSraDto | null;
  /** True when no ELIGIBLE row is flagged as master — the only case an auto-assign may happen. */
  masterWasMissing: boolean;
  /** Rows flagged as master that must not be (wrong AccessType) — their flag should be cleared. */
  ineligibleFlaggedRows: AccountSraDto[];
}

export const resolveMaster = (rows: AccountSraDto[]): MasterResolution => {
  const flaggedEligible = rows.filter((row) => isFlaggedMaster(row) && isMasterEligible(row)).sort(byPreference);
  const ineligibleFlaggedRows = rows.filter((row) => isFlaggedMaster(row) && !isMasterEligible(row));

  if (flaggedEligible.length > 0) {
    return { master: flaggedEligible[0], masterWasMissing: false, ineligibleFlaggedRows };
  }

  const candidate = rows.filter(isMasterEligible).sort(byPreference)[0] ?? null;
  return { master: candidate, masterWasMissing: true, ineligibleFlaggedRows };
};
